import { FC, useContext, createContext, PropsWithChildren, useCallback } from 'react';
import { ListType } from 'modules/domains/list';
import { noop } from 'utils/noop';
import {
  useSearchParamState,
  serializationOptionsForStringValue,
  createSerializationOptionsForEnumArrayValue,
  serializationOptionsForNumberArrayValue,
} from 'utils/hooks/useSearchParamState';
import { LIST_QUERY_PARAMS } from '../types';

export type FiltersContextState = {
  search: string;
  setSearch: (search: string) => void;
  types: ListType[];
  setTypes: (type: ListType[]) => void;
  listIds: number[];
  setListIds: (lists: number[]) => void;
  resetFilters: () => void;
};

export const defaultFiltersState: FiltersContextState = {
  search: '',
  types: [],
  listIds: [],
  setListIds: noop,
  setSearch: noop,
  setTypes: noop,
  resetFilters: noop,
};

export const FiltersContext = createContext<FiltersContextState>(defaultFiltersState);
export const useFiltersContext = () => useContext(FiltersContext);

export const FiltersContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [search, setSearch] = useSearchParamState({
    searchParamKey: LIST_QUERY_PARAMS.search,
    stateValueWhenSearchParamIsUnset: '',
    ...serializationOptionsForStringValue,
  });

  const [types, setTypes] = useSearchParamState({
    searchParamKey: LIST_QUERY_PARAMS.type,
    stateValueWhenSearchParamIsUnset: [],
    ...createSerializationOptionsForEnumArrayValue({
      enumKeyValueMap: ListType,
    }),
  });

  const [listIds, setListIds] = useSearchParamState({
    searchParamKey: LIST_QUERY_PARAMS.list,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForNumberArrayValue,
  });

  const resetFilters = useCallback(() => {
    setSearch('');
    setTypes([]);
    setListIds([]);
  }, [setSearch, setTypes, setListIds]);

  const value = {
    search,
    setSearch,
    types,
    listIds,
    setTypes,
    setListIds,
    resetFilters,
  };

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};
